import { applyAgentChatParam } from './agentChatParam';

/**
 * Events the shell-hosted agent chat app raises via its `onAgentEvent`
 * customProp. The agent describes what the user wants in durable Robin
 * identifiers; the shell decides which dashboard surface handles it.
 *
 * These types structurally mirror `AgentShellEvent` in robin-ai-agents
 * (web/src/utils/shellBridge.ts). They are duplicated because that repo is
 * not a workspace here, so there is no shared package both sides can import
 * (unlike the auth bridge types, which live once in configTypes.ts).
 * Additive changes only: a newer agent bundle may emit event types this
 * shell doesn't know yet, so unknown types must be ignored, and payload
 * fields must never be renamed, removed, or retyped.
 */

export type ViewDeskAgentEvent = {
  type: 'viewDesk';
  payload: {
    deskId: string;
    locationId?: string;
    levelId?: string;
  };
};

export type ViewSpaceAgentEvent = {
  type: 'viewSpace';
  payload: {
    spaceId: string;
    locationId?: string;
    levelId?: string;
  };
};

export type AgentShellEvent = ViewDeskAgentEvent | ViewSpaceAgentEvent;

/**
 * Office-app deep link selecting a resource on the map, via the same
 * `locations`/`levels`/`csr` params the people-profile sidebar uses
 * (office handles them in SelectResourceFromURL / seedFromSearchParams).
 *
 * The assistant param is carried on the link so the navigation itself never
 * closes the panel; whether it stays open is then the office arbitration's
 * call (single right-hand sider, last-opened-wins — see office's
 * RightSidebar): an in-session selection edge makes office ask the assistant
 * to yield, so the resource's details sider shows; a cross-app deep link
 * seeds the selection at mount (no edge), so the assistant stays open with
 * the details sider suspended until it's closed.
 *
 * `csrType` is the office map layer (`seats` for desks, `spaces` for spaces).
 */
const buildViewResourcePath = (
  orgSlug: string,
  csrType: 'seats' | 'spaces',
  resourceId: string,
  payload: { locationId?: string; levelId?: string }
): string => {
  // Seed from the live URL (the same rule as setAgentChatParam): office
  // URL-syncs its date/time state via recoil-sync, which resets any synced
  // param missing from the new URL — building from scratch would silently
  // snap the map back to today.
  const params = new URLSearchParams(window.location.search);
  if (payload.locationId) {
    params.set('locations', payload.locationId);
    // A level carried over from the current URL belongs to the previous
    // location; drop it unless the event names one.
    if (!payload.levelId) {
      params.delete('levels');
    }
  }
  if (payload.levelId) {
    params.set('levels', payload.levelId);
  }
  // String() because the payload crosses a repo boundary at runtime — office
  // paths that read `csr` don't all coerce a numeric id.
  params.set('csr', JSON.stringify({ type: csrType, id: String(resourceId) }));
  applyAgentChatParam(params);
  return `/${orgSlug}/officeV2?${params.toString()}`;
};

export const buildViewDeskPath = (
  orgSlug: string,
  payload: ViewDeskAgentEvent['payload']
): string => buildViewResourcePath(orgSlug, 'seats', payload.deskId, payload);

export const buildViewSpacePath = (
  orgSlug: string,
  payload: ViewSpaceAgentEvent['payload']
): string => buildViewResourcePath(orgSlug, 'spaces', payload.spaceId, payload);
